import type { LearnTopic } from "../topic-types";

/**
 * Boundaries After Trauma.
 */
export const BOUNDARIES_TOPIC: LearnTopic = {
  slug: "boundaries",
  emoji: "🚪",
  title: "Boundaries After Trauma",
  tagline: "Protecting your space without guilt",
  theme: "gold",
  category: "bonds-boundaries",
  seoDescription:
    "Understand why boundaries feel hard after trauma, learn practical scripts for work, family, dating and digital life, and use a printable worksheet to plan your own. Trauma-informed education from Recovery Trauma™.",
  cardBlurb: "Practical scripts and tools for setting boundaries without guilt.",
  isNew: true,
  intro: [
    "A boundary is simply a way of saying what is okay for you and what isn't — with your time, your body, your emotions, your space, your attention. Boundaries aren't walls to keep people out; they're more like doors with a lock you get to decide whether to use.",
    "If boundaries feel unnatural, frightening, or like they will cost you a relationship, that often makes complete sense in the context of what you've been through. If saying no once led to punishment, withdrawal, ridicule or danger, then your nervous system learned that boundaries were unsafe — not that you're bad at having them.",
    "This guide covers what boundaries are, why they can feel so hard to hold after trauma, and practical language you can borrow or adapt. It also includes an honest note: sometimes setting a boundary directly isn't safe, and that isn't a failure on your part either.",
  ],
  closing:
    "Boundaries are a skill you build gradually, not a personality trait you either have or don't. Small, low-stakes practice counts. If you are in an unsafe or abusive situation, please see our note on safety in this guide and visit Crisis Resources — your safety comes first.",
  continueTo: [
    { label: "People-Pleasing", to: "/learn/people-pleasing" },
    { label: "Trauma Bonds", to: "/learn/trauma-bonds" },
    { label: "Trauma & Relationships", to: "/learn/trauma-relationships" },
    { label: "Journal", to: "/journal" },
    { label: "Crisis Resources", to: "/crisis" },
  ],
  sections: [
    {
      slug: "understand",
      emoji: "📚",
      title: "Understand: what boundaries are",
      blurb: "The different kinds of boundaries, and why they can feel unsafe after trauma.",
      intro:
        "Boundaries aren't one single thing — they show up across several areas of life. Understanding the different types can help you notice where yours feel strongest and where they feel hardest to hold.",
      resources: [
        {
          slug: "what-are-boundaries",
          kind: "learn",
          title: "What is a boundary, really?",
          summary: "A plain-English definition, and why boundaries protect connection rather than block it.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "A boundary is a limit you set about what you will and won't accept, do, or allow into your space. It can be spoken out loud, or it can be an internal decision that shapes your choices — like deciding not to answer work messages after 7pm, even if you never explain why.",
            },
            {
              type: "list",
              title: "Boundaries can be about",
              items: [
                "Physical space — touch, proximity, privacy, your home",
                "Emotional space — what feelings you take responsibility for, and whose you don't",
                "Time — how much of it you give, and to whom",
                "Digital life — messages, social media, being contactable",
                "Relationships — what behaviour you will and won't accept from others",
              ],
            },
            {
              type: "note",
              text: "A boundary is about what you will do, not about controlling what someone else does. \"Don't raise your voice at me\" is a request; \"if you raise your voice, I'll leave the room\" is a boundary you can actually keep.",
            },
          ],
        },
        {
          slug: "types-of-boundaries",
          kind: "learn",
          title: "The main types of boundaries",
          summary: "Physical, emotional, digital, time and relationship boundaries, explained.",
          minutes: 5,
          blocks: [
            {
              type: "list",
              title: "Physical boundaries",
              items: [
                "Who can touch you, hug you, or stand close to you, and when",
                "Privacy around your body, your bedroom, your belongings",
                "The right to say \"not right now\" to physical affection, even from people you love",
              ],
            },
            {
              type: "list",
              title: "Emotional boundaries",
              items: [
                "Not taking responsibility for other people's moods or reactions",
                "Choosing what personal information you share, and with whom",
                "Allowing yourself to feel differently to someone else without needing to justify it",
              ],
            },
            {
              type: "list",
              title: "Digital boundaries",
              items: [
                "Muting, unfollowing or blocking without needing to explain",
                "Choosing when you're contactable, and switching notifications off",
                "Deciding what parts of your life you share online",
              ],
            },
            {
              type: "list",
              title: "Time boundaries",
              items: [
                "How much notice you need before agreeing to something",
                "Ending conversations, calls or visits when you're tired",
                "Protecting time for rest, work, or things that matter to you",
              ],
            },
            {
              type: "list",
              title: "Relationship boundaries",
              items: [
                "What tone of voice, language or behaviour you'll accept",
                "How much closeness, contact or disclosure feels right at this pace",
                "Being able to disagree without the relationship being at risk",
              ],
            },
          ],
        },
        {
          slug: "why-saying-no-feels-unsafe",
          kind: "learn",
          title: "Why saying no can feel unsafe",
          summary: "How early experiences can wire boundaries to danger rather than safety.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "If, growing up or in a past relationship, saying no led to anger, punishment, withdrawal of love, mockery, or worse, your body may have learned that a boundary is the thing that causes harm — rather than the thing that prevents it. That association can persist long after the environment that created it has changed.",
            },
            {
              type: "list",
              title: "This can show up as",
              items: [
                "A racing heart or shaky voice before you've even said anything",
                "Agreeing to something the second you feel any tension in the room",
                "Rehearsing a no for hours and still not saying it",
                "Feeling like you owe someone an explanation for existing with a limit",
                "A flood of dread or shame after you do manage to say no",
              ],
            },
            {
              type: "note",
              text: "This is a trauma response, not a character flaw. Naming it as a nervous system pattern — rather than a personal failing — is often the first step toward loosening its grip.",
            },
          ],
        },
        {
          slug: "recognising-violations",
          kind: "learn",
          title: "Recognising when a boundary has been crossed",
          summary: "Signs a limit has been ignored or overridden, even subtly.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Signs a boundary has been violated",
              items: [
                "You said no, and the person kept pushing, joking, or guilt-tripping until you gave in",
                "Your \"no\" was treated as the start of a negotiation rather than an answer",
                "You feel resentful, drained, or unlike yourself after time with someone",
                "You're punished, sulked at, or frozen out after asserting a limit",
                "Someone reads your messages, enters your space, or shares your information without asking",
              ],
            },
            {
              type: "prose",
              text: "It can take time to recognise a violation, especially if minimising other people's behaviour has been a survival strategy. Trusting a feeling of discomfort — even before you can explain it — is a valid starting point.",
            },
          ],
        },
      ],
    },
    {
      slug: "recognise",
      emoji: "🔎",
      title: "Recognise: guilt, over-explaining and people-pleasing",
      blurb: "The patterns that make holding a boundary feel so uncomfortable.",
      resources: [
        {
          slug: "guilt-after-a-boundary",
          kind: "reflect",
          title: "Guilt after setting a boundary",
          summary: "Understanding — and sitting with — the guilt that often follows a boundary.",
          minutes: 6,
          blocks: [
            {
              type: "prose",
              text: "Feeling guilty after a boundary doesn't mean you did something wrong. For many trauma survivors, guilt is simply the sound of an old rule being broken: \"put others first,\" \"don't cause a scene,\" \"keep everyone comfortable.\" The guilt is real, but it isn't proof of harm.",
            },
            {
              type: "prompts",
              title: "Reflection prompts",
              items: [
                "What did I actually do — describe it without judgement, as if reporting facts?",
                "Did I lie, harm, or manipulate anyone — or did I simply say what was true for me?",
                "Whose voice does the guilt sound like?",
                "What would I say to a friend who felt guilty for doing the same thing?",
                "Can I let the guilt be present without letting it undo the boundary?",
              ],
            },
            {
              type: "note",
              text: "Guilt after a boundary often fades with repetition, as your nervous system learns that the feared consequence didn't happen. That doesn't mean it will disappear instantly, and it's okay if it takes practice.",
            },
          ],
        },
        {
          slug: "over-explaining",
          kind: "learn",
          title: "Over-explaining",
          summary: "Why \"no\" so often turns into three paragraphs of justification.",
          minutes: 4,
          blocks: [
            {
              type: "prose",
              text: "Over-explaining is often an attempt to make a boundary so airtight and reasonable that no one could possibly be upset by it. It can feel like a safety strategy, but it usually invites debate — because a long justification gives someone more to argue with.",
            },
            {
              type: "list",
              title: "Signs of over-explaining",
              items: [
                "Giving three reasons when zero or one would do",
                "Apologising more than once for the same limit",
                "Offering an alternative before anyone's asked for one",
                "Feeling the need to prove your no is valid",
              ],
            },
            {
              type: "prose",
              text: "A full sentence is a complete boundary. \"I can't make it Friday\" doesn't need a supporting essay. If you want to offer a reason, one honest sentence is usually enough.",
            },
          ],
        },
        {
          slug: "people-pleasing-link",
          kind: "learn",
          title: "How people-pleasing gets in the way",
          summary: "The connection between fawning, boundaries, and losing touch with your own needs.",
          minutes: 3,
          blocks: [
            {
              type: "prose",
              text: "People-pleasing and boundary-setting sit at opposite ends of the same spectrum. If your safety once depended on reading a room and keeping everyone content, boundaries can feel like abandoning the very strategy that helped you survive.",
            },
            {
              type: "note",
              text: "For a deeper look at this pattern, see our People-Pleasing After Trauma guide — the two topics work well together.",
            },
          ],
        },
      ],
    },
    {
      slug: "practise",
      emoji: "🌿",
      title: "Practise: scripts and small steps",
      blurb: "Language you can borrow, and a way to start small.",
      resources: [
        {
          slug: "core-boundary-scripts",
          kind: "practice",
          title: "Core boundary scripts",
          summary: "Simple, complete sentences you can use as they are, or adapt.",
          minutes: 5,
          blocks: [
            {
              type: "prose",
              text: "These sentences are deliberately short. A boundary doesn't need to be softened with three apologies to be kind — it needs to be clear.",
            },
            {
              type: "list",
              title: "General-purpose scripts",
              items: [
                "\"I'm not comfortable with that.\"",
                "\"I need some time before I answer.\"",
                "\"No, I can't do that.\"",
                "\"I don't want to discuss this.\"",
                "\"That doesn't work for me.\"",
                "\"I hear you, and my answer is still no.\"",
                "\"I'm going to end the conversation here.\"",
              ],
            },
            {
              type: "note",
              text: "You are allowed to repeat the same sentence more than once if someone doesn't accept your first answer. Repetition is a boundary tool, not rudeness.",
            },
          ],
        },
        {
          slug: "scripts-for-work",
          kind: "practice",
          title: "Scripts for work",
          summary: "Language for workload, hours, and workplace pressure.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "At work",
              items: [
                "\"I can't take that on right now without dropping something else — can we talk about priorities?\"",
                "\"I'm not able to stay late tonight.\"",
                "\"I'll pick this up again during working hours.\"",
                "\"I'd rather we discuss this over email so I have time to think it through.\"",
                "\"I'm not going to be able to give feedback on that today.\"",
              ],
            },
          ],
        },
        {
          slug: "scripts-for-family",
          kind: "practice",
          title: "Scripts for family",
          summary: "Language for family situations, where history can make boundaries harder.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "With family",
              items: [
                "\"I love you, and I'm not going to discuss that topic.\"",
                "\"I won't be coming this year, but I hope you have a good time.\"",
                "\"Please don't comment on my weight/relationship/parenting.\"",
                "\"I need you to knock before coming into my room.\"",
                "\"I'm going to leave if the conversation carries on like this.\"",
              ],
            },
            {
              type: "note",
              text: "Family boundaries can bring up guilt or grief for the relationship you wish you had. Both feelings can be true at once — you can love someone and still hold a limit with them.",
            },
          ],
        },
        {
          slug: "scripts-for-dating",
          kind: "practice",
          title: "Scripts for dating and relationships",
          summary: "Language for pace, intimacy, and early relationship boundaries.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "In dating or relationships",
              items: [
                "\"I'd like to take this a bit slower.\"",
                "\"I'm not ready to share that yet.\"",
                "\"I need us not to text every day — I still want to see you.\"",
                "\"I'm not comfortable with that kind of joke about me.\"",
                "\"I need some space tonight, it isn't about you.\"",
              ],
            },
          ],
        },
        {
          slug: "scripts-for-digital",
          kind: "practice",
          title: "Scripts for digital contexts",
          summary: "Language for messages, social media, and being constantly contactable.",
          minutes: 4,
          blocks: [
            {
              type: "list",
              title: "Online and by message",
              items: [
                "\"I don't check messages after work hours, I'll reply tomorrow.\"",
                "\"I'm taking a break from this group chat for a while.\"",
                "\"I'd rather not have this conversation over text.\"",
                "\"Please don't tag me in posts without asking first.\"",
                "\"I'm going to mute this thread — nothing personal.\"",
              ],
            },
            {
              type: "note",
              text: "You never owe anyone an explanation for muting, unfollowing or blocking. Digital boundaries are still boundaries, even without a spoken conversation.",
            },
          ],
        },
        {
          slug: "start-small-practice",
          kind: "practice",
          title: "Start small: a boundary-practice exercise",
          summary: "Building the muscle with low-stakes situations first.",
          minutes: 7,
          blocks: [
            {
              type: "prose",
              text: "Boundaries are a skill, and skills are easier to build on easy ground first. Practising with low-stakes situations — where the outcome doesn't matter much — helps your nervous system learn that a boundary can be survived.",
            },
            {
              type: "steps",
              title: "A gentle way to begin",
              items: [
                { label: "Pick something low-stakes", note: "A takeaway order, a shop assistant, a small preference among friends." },
                { label: "Choose one short script", note: "\"No thank you\" or \"that doesn't work for me\" is plenty." },
                { label: "Say it once, without a paragraph of reasons", note: "Notice the urge to over-explain, and try leaving it out." },
                { label: "Let the discomfort exist", note: "You don't need it to disappear for the boundary to count." },
                { label: "Notice what actually happened", note: "Often it's far less dramatic than feared." },
                { label: "Build up gradually", note: "Move toward higher-stakes boundaries only as it feels manageable." },
              ],
            },
          ],
        },
      ],
    },
    {
      slug: "reflect-and-plan",
      emoji: "✍️",
      title: "Reflect and plan",
      blurb: "Making sense of your own patterns, and planning a boundary in advance.",
      resources: [
        {
          slug: "communicating-boundaries",
          kind: "learn",
          title: "Communicating a boundary clearly",
          summary: "A simple structure for saying what you need without over-explaining.",
          minutes: 4,
          blocks: [
            {
              type: "steps",
              title: "A simple structure",
              items: [
                { label: "Name it plainly", note: "\"I need...\" or \"I'm not able to...\"" },
                { label: "Keep the reason short, if you give one at all", note: "One sentence, not a defence." },
                { label: "State what you'll do", note: "\"So I'm going to...\" — this keeps the boundary about your own action." },
                { label: "Stop talking", note: "Resist the urge to fill silence with more justification." },
              ],
            },
            {
              type: "quote",
              text: "\"I need to leave by 6. I'll head off then, but let's grab the next hour.\"",
            },
          ],
        },
        {
          slug: "safety-note",
          kind: "learn",
          title: "A note on safety",
          summary: "When a direct boundary may not be safe, and where to get support.",
          minutes: 3,
          blocks: [
            {
              type: "note",
              text: "In some relationships — particularly ones involving coercive control, intimidation, or abuse — setting a boundary directly can increase risk rather than reduce it. If this might be your situation, please don't feel you have to \"just say no\" as advice on this page suggests. Your safety comes first, and planning safely often looks different to standard boundary advice.",
            },
            {
              type: "prose",
              text: "If you're unsure whether a relationship is safe, or you want to talk something through, our Crisis Resources page has options for immediate and ongoing support.",
            },
            { type: "list", title: "If you are ever in immediate danger", items: ["Please contact emergency services or visit our Crisis Resources page for further options."] },
          ],
        },
        {
          slug: "reflection-guilt-and-history",
          kind: "reflect",
          title: "Reflecting on your own boundary history",
          summary: "Understanding where your patterns around boundaries began.",
          minutes: 6,
          blocks: [
            {
              type: "prompts",
              items: [
                "Growing up, what happened when someone in my life said no?",
                "Who in my life models boundaries well, and what do I notice about them?",
                "Which relationship in my life currently feels hardest to set a limit in — and why might that be?",
                "What's one small boundary I've held recently that I could acknowledge myself for?",
              ],
            },
          ],
        },
        {
          slug: "boundary-worksheet",
          kind: "worksheet",
          title: "My boundary plan",
          summary: "A printable worksheet for planning a boundary before a conversation.",
          minutes: 8,
          worksheetFields: [
            { label: "The boundary I want to set", hint: "Describe it in one or two sentences.", lines: 3 },
            { label: "Who it involves", hint: "The person or people this boundary relates to.", lines: 2 },
            { label: "What I'll say", hint: "Write the exact words, kept short.", lines: 3 },
            { label: "When and where I'll say it", hint: "Choosing the moment can make it easier.", lines: 2 },
            { label: "What I'll do if it isn't respected", hint: "E.g. repeat the sentence, end the conversation, leave the room.", lines: 3 },
            { label: "How I'll support myself afterwards", hint: "A grounding exercise, a message to a friend, some rest.", lines: 2 },
          ],
        },
      ],
    },
  ],
};
